import type { Instrument } from "../data/instruments";
import { fallbackNotes } from "./notes";

export const primaryPerformanceKeys = ["a", "s", "d", "f", "j", "k", "l", ";"] as const;

export type PrimaryPerformanceKey = (typeof primaryPerformanceKeys)[number];

export interface KeyboardPerformanceMapping {
  key: PrimaryPerformanceKey;
  noteName: string;
  label: string;
  role: string;
  group: "left" | "right" | "center";
  shiftRole: string;
  shiftOrnament: string;
}

const defaultRoles = [
  "primary 1",
  "primary 2",
  "primary 3",
  "primary 4",
  "primary 5",
  "primary 6",
  "primary 7",
  "primary 8"
];

const rolesByInstrument: Record<string, string[]> = {
  gayageum: ["string 1", "string 2", "string 3", "string 4", "string 5", "string 6", "string 7", "string 8"],
  erhu: ["low position", "2nd position", "3rd position", "4th position", "5th position", "6th position", "7th position", "high position"],
  oud: ["course 1", "course 2", "course 3", "course 4", "course 5", "course 6", "maqam color 1", "maqam color 2"],
  sitar: ["Sa", "Re", "Ga", "Ma", "Pa", "Dha", "Ni", "Sa high"],
  daegeum: ["jigong 1", "jigong 2", "jigong 3", "jigong 4", "jigong 5", "jigong 6", "breath tone", "cheong color"],
  ney: ["maqam note 1", "maqam note 2", "maqam note 3", "maqam note 4", "maqam note 5", "maqam note 6", "upper note", "breath color"],
  janggu: ["gung low", "deon low", "kung low", "buk low", "chae high", "ddak high", "deok high", "chaepyeon high"],
  tabla: ["bayan ge", "bayan ke", "bayan ghe", "bayan pressure", "dayan na", "dayan tin", "dayan ta", "dayan syahi"],
  mbira: ["left lower 1", "left lower 2", "left upper 1", "left upper 2", "right anchor 1", "right anchor 2", "right extension", "right index"],
  kora: ["left row 1", "left row 2", "left row 3", "left row 4", "right row 1", "right row 2", "right row 3", "right row 4"],
  yidaki_didgeridoo: ["vocal A", "vocal E", "vocal O", "vocal pulse", "tongue pulse", "drone color", "harmonic", "accent"],
  gamelan_saron: ["bar 1", "bar 2", "bar 3", "bar 4", "bar 5", "bar 6", "bar 7", "open color"]
};

const shiftByInstrument: Record<string, { role: string; ornament: string }> = {
  gayageum: { role: "jeonseong quick roll", ornament: "jeonseong" },
  erhu: { role: "rou xian accent", ornament: "rou_xian" },
  oud: { role: "tremolo", ornament: "tremolo" },
  sitar: { role: "chikari", ornament: "chikari" },
  daegeum: { role: "cheong buzz accent", ornament: "cheong_accent" },
  ney: { role: "overblow color", ornament: "overblow" },
  janggu: { role: "strong stroke", ornament: "strong_stroke" },
  tabla: { role: "combined bol", ornament: "combined_bol" },
  mbira: { role: "buzz accent", ornament: "buzz_accent" },
  kora: { role: "arpeggio accent", ornament: "arpeggio" },
  yidaki_didgeridoo: { role: "drone accent", ornament: "drone_accent" },
  gamelan_saron: { role: "open sustain hit", ornament: "open_sustain" }
};

export function getKeyboardPerformanceMappings(instrument: Instrument): KeyboardPerformanceMapping[] {
  const roles = rolesByInstrument[instrument.id] ?? defaultRoles;
  const shift = shiftByInstrument[instrument.id] ?? { role: "accent", ornament: "accent" };
  const notes = instrument.playableNotes.length > 0 ? instrument.playableNotes : fallbackNotes;

  return primaryPerformanceKeys.map((key, index) => ({
    key,
    noteName: notes[index % notes.length],
    label: key === ";" ? ";" : key.toUpperCase(),
    role: roles[index] ?? defaultRoles[index],
    group: index < 4 ? "left" : "right",
    shiftRole: shift.role,
    shiftOrnament: shift.ornament
  }));
}

export function isPerformanceTextInput(target: EventTarget | null): boolean {
  const element = target instanceof HTMLElement ? target : null;

  if (!element) {
    return false;
  }

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement ||
    element.isContentEditable
  );
}
